// React import
import { useState, useEffect } from 'react';

// Generic imports
import _ from 'lodash';
import axios from 'axios';
import { useOutletContext } from 'react-router-dom';

// MUI imports
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { green, red } from '@mui/material/colors';

// Font Awesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileCheck, faPen, faTrash } from '@fortawesome/pro-light-svg-icons';
import { faCheck, faXmark, faPlus } from '@fortawesome/free-solid-svg-icons';

// Local imports
import Header from '../Header';

const SCREENS = [{
    key: 'price-quote',
    title: 'Price Quotes Terms',

}, {
    key: 'price-quote-air',
    title: 'Price Quotes Terms (Air)',

}, {
    key: 'price-quote-sea',
    title: 'Price Quotes Terms (Sea)',
}, {
    key: 'price-quote-land',
    title: 'Price Quotes Terms (Land)',
}, {
    key: 'agent-query',
    title: 'Agent Query Terms',
}, {
    key: 'others',
    title: 'Other Terms',
}]

function Terms() {
    const { org, accesstoken } = useOutletContext();
    const [ screen, setScreen ] = useState('price-quote');
    const [ html, setHtml ] = useState(_.fromPairs(_.map(_.uniq([...SCREENS.map(s => s.key), ..._.keys(org.terms)]), s => ( [s, (org.terms || {})[s] || '']))));
    const [ saving, setSaving ] = useState(false);
    const [ savingSuccess, setSavingSuccess ] = useState(null);
    const [ errors, setErrors ] = useState([]);
    const [ subscreen, setSubscreen ] = useState(null);
    const [ rename, setRename ] = useState(false);
    const [ erase, setErase ] = useState(false);

    async function onSave() {
        setSaving(true);
        try {
            await axios.post(`/api/organizations/${org.id}/terms?accesstoken=${accesstoken}`, html);
            setSaving(false);
            setSavingSuccess('yes');
        } catch(e) {
            setSaving(false);
            setSavingSuccess('no');
            setErrors([e.message]);
            console.log('Error saving:', e);
        }

        await new Promise(res => setTimeout(res, 3000));
        setSavingSuccess(null);
    }

    function onAddSubscreen() {
        let i;
        for (i = 1 ; i < 100 ; i++) {
            if (!_.keys(html).includes(`New Terms ${i}`)) break;

        }
        setHtml({...html, [`New Terms ${i}`]: ''});
        setSubscreen(`New Terms ${i}`);
    }

    return (
        <Box>
            <Header
                icon={faFileCheck}
                title={'Terms & Conditions'}
                tabs={_.map(SCREENS, s => ({
                    title: s.title,
                    action: () => setScreen(s.key),
                    selected: s.key === screen
                }))}
                buttons={[{
                    title: 'Save',
                    icon: savingSuccess === 'yes' ? faCheck : savingSuccess === 'no' ? faXmark : null,
                    iconColor: savingSuccess === 'yes' ? green[500] : savingSuccess === 'no' ? red[500] : null,
                    width: '80px',
                    color: 'monada-button',
                    action: onSave,
                    thinking: saving,
                    disabled: saving || !!savingSuccess,
                }]}
            />

            {errors.length > 0 && (
                <Alert severity='error' sx={{ marginBottom: '12px' }}>
                    <div>
                        Sorry, there was an error saving:<br/>
                    </div>
                    <ul>
                        {errors.map(e => (
                            <li key={e}>{e}</li>
                        ))}
                    </ul>
                </Alert>
            )}

            {screen === 'others' && (
                <Box sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex' }}>
                        <FormControl fullWidth>
                            <InputLabel>Select Terms</InputLabel>
                            <Select
                                value={subscreen || ''}
                                onChange={(event) => {
                                    setSubscreen(event.target.value);
                                }}
                                label='Select Terms'
                            >
                                {_.map(_.without(_.keys(html), 'price-quote', 'price-quote-air', 'price-quote-sea',
                                    'price-quote-land', 'agent-query', 'others'), t => (
                                    <MenuItem key={t} value={t}>{t}</MenuItem>
                                ))} 
                            </Select>
                        </FormControl>
                        <IconButton
                            color='primary'
                            variant='contained'
                            style={{ marginLeft: '12px' }}
                            onClick={onAddSubscreen}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                        </IconButton>

                        <IconButton
                            disabled={!subscreen}
                            color='secondary'
                            sx={{ marginLeft: '12px' }}
                            onClick={() => setRename(true)}
                        >
                            <FontAwesomeIcon icon={faPen} />
                        </IconButton>

                        <IconButton
                            disabled={!subscreen}
                            color='error'
                            sx={{ marginLeft: '12px' }}
                            onClick={() => setErase(true)}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </IconButton>
                    </Box>
                </Box>
            )}

            {(screen !== 'others' || subscreen) && (
                <Box sx={{ p: 3, pt: 0 }}>
                    <Box>
                        <TextField
                            fullWidth
                            label='Header HTML'
                            multiline
                            rows={4}
                            value={html[screen !== 'others' ? screen : subscreen] || ''}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setHtml({...html, [screen !== 'others' ? screen : subscreen]: event.target.value});
                            }}
                        />
                    </Box>

                    <Box style={{ paddingTop: '24px' }}>
                        <Typography variant='h6'>Result</Typography>
                        <div dangerouslySetInnerHTML={{ __html: html[screen !== 'others' ? screen : subscreen] }} />
                    </Box>
                </Box>
            )}

            <RenameDialog initialText={subscreen} open={rename} onClose={() => setRename(false)} onSave={(title) => {
                setHtml(h => _.omit({ ...h, [title]: h[subscreen] }, subscreen));
                setSubscreen(title);
            }} />

            <EraseDialog title={subscreen} open={erase} onClose={() => setErase(false)} onErase={() => {
                const keys = _.keys(html);
                setHtml(h => _.omit(h, subscreen));
                setSubscreen(keys.indexOf(subscreen) > -1 ? keys[keys.indexOf(subscreen) - 1] : 'null');
            }} />

        </Box>
    )
}

function RenameDialog(props) {
    const { open, onClose, onSave, initialText } = props;
    const [ title, setTitle ] = useState('');

    useEffect(() => {
        setTitle(initialText);
    }, [initialText]);

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Rename Terms</DialogTitle>
            <DialogContent sx={{ paddingTop: '12px !important' }}>
                <TextField
                    fullWidth
                    label='Title'
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={() => {
                    onSave(title);
                    onClose();
                }} sx={{ fontFamily: 'Poppins, sans-serif' }}>Save</Button>
            </DialogActions>
        </Dialog>
    )
}

function EraseDialog(props) {
    const { open, onClose, onErase, title } = props;

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Erase Terms</DialogTitle>
            <DialogContent>
                Are you sure you want to erase the terms <b>{title}</b>?
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={() => {
                    onErase();
                    onClose();
                }}>Erase</Button>
            </DialogActions>
        </Dialog>
    )
}

export default Terms;
